import './MapOverview.css';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L, { Marker as LeafletMarker } from 'leaflet';
import { BUILDING_MARKER_COLOR, createSvgMarker, markerSymbolUrl } from './CustomIcon';
import { Dropdown, Form } from "react-bootstrap";
import MapPopup from "./MapPopup";
import { BuildingSummary } from "../types/protocols";
import { loadFromStorage, saveToStorage, STORAGE_KEYS } from "../hooks/mapOverview/useMapOverview";

// Label collision detection: padding around bounding boxes
const COLLISION_PADDING = 4;

interface BoundingBox {
    left: number;
    right: number;
    top: number;
    bottom: number;
}

function boxesOverlap(a: BoundingBox, b: BoundingBox): boolean {
    return !(a.right < b.left || b.right < a.left || a.bottom < b.top || b.bottom < a.top);
}

function getMarkerBoundingBox(markerEl: HTMLElement): BoundingBox {
    const rect = markerEl.getBoundingClientRect();
    return {
        left: rect.left - COLLISION_PADDING,
        right: rect.right + COLLISION_PADDING,
        top: rect.top - COLLISION_PADDING,
        bottom: rect.bottom + COLLISION_PADDING,
    };
}

function getLabelBoundingBox(labelEl: HTMLElement): BoundingBox {
    const labelRect = labelEl.getBoundingClientRect();
    return {
        left: labelRect.left - COLLISION_PADDING,
        right: labelRect.right + COLLISION_PADDING,
        top: labelRect.top - COLLISION_PADDING,
        bottom: labelRect.bottom + COLLISION_PADDING,
    };
}

const MARKER_COLORS = {
    active: BUILDING_MARKER_COLOR,
    greyed: '#777777',
} as const;

const MARKER_OPACITY = {
    full: 1,
    filteredOut: 0.2,
} as const;

const MARKER_Z_INDEX = {
    filteredOut: -1000,
    normal: 0,
    highlighted: 100,
} as const;

/** Updates opacity and label visibility for a filtered-out marker */
function setFilteredMarkerVisibility(marker: LeafletMarker | null, visible: boolean): void {
    if (!marker) return;
    marker.setOpacity(visible ? MARKER_OPACITY.full : MARKER_OPACITY.filteredOut);
    const labelEl = marker.getElement()?.querySelector<HTMLElement>('.map-marker-label');
    if (labelEl) labelEl.style.display = visible ? '' : 'none';
}

interface CoordinateItem {
    coords: [number, number];
    building: BuildingSummary;
    isFiltered?: boolean;
}

interface MapComponentProps {
    allCoordinates: CoordinateItem[];
    focusCoordinates: { coords: [number, number] }[];
    highlightedId?: string;
    popupInfo?: { id: string; coordinates: [number, number] } | null;
    onMarkerClick?: (id: string) => void;
    onPopupClose?: () => void;
    shouldResizeBounds: boolean;
    buildingSummaries?: BuildingSummary[];
}





const FitBoundsHandler: React.FC<{ bounds: L.LatLngBoundsExpression, shouldResizeBounds: Boolean }> = ({ bounds, shouldResizeBounds }) => {
    const map = useMap();
    const previousBoundsRef = useRef<string | undefined>(undefined);

    useEffect(() => {
        if (!shouldResizeBounds) {
            return;
        }

        const boundsString = JSON.stringify(bounds);
        if (Array.isArray(bounds) && bounds.length > 0 && boundsString !== previousBoundsRef.current) {
            map.fitBounds(bounds, { padding: [20, 20] });
            previousBoundsRef.current = boundsString;
        }
    }, [bounds, map, shouldResizeBounds]);

    return null;
};

const LabelCollisionHandler: React.FC<{
    markerRefs: React.MutableRefObject<{ [key: string]: LeafletMarker | null }>;
    markerCount: number;
    highlightedId?: string;
    hoveredId?: string;
    allCoordinates: CoordinateItem[];
}> = ({ markerRefs, markerCount, highlightedId, hoveredId, allCoordinates }) => {
    const map = useMap();

    const updateLabelVisibility = useCallback(() => {
        const markers = markerRefs.current;
        const markerEntries: Array<{ key: string; marker: LeafletMarker; screenY: number; forceShow: boolean }> = [];

        // Pre-compute filtered keys set for O(1) lookups instead of O(n) .find() per marker
        const filteredKeys = new Set(
            allCoordinates.filter(c => c.isFiltered).map(c => c.building.key)
        );

        // Collect all markers with their screen positions
        // Skip filtered-out markers - they don't participate in collision detection
        for (const [key, marker] of Object.entries(markers)) {
            if (!marker) continue;
            // Skip filtered-out markers from collision detection (O(1) lookup)
            if (!filteredKeys.has(key)) continue;
            const el = marker.getElement();
            if (!el) continue;
            const rect = el.getBoundingClientRect();
            const forceShow = key === highlightedId || key === hoveredId;
            // Use bottom of marker for Y sorting (lower on screen = higher Y value = should render first)
            markerEntries.push({ key, marker, screenY: rect.bottom, forceShow });
        }

        // Sort: force-show markers first, then by screenY descending (bottom-most markers first)
        markerEntries.sort((a, b) => {
            if (a.forceShow !== b.forceShow) return a.forceShow ? -1 : 1;
            return b.screenY - a.screenY;
        });

        const occupiedBoxes: BoundingBox[] = [];

        for (const { marker, forceShow } of markerEntries) {
            const el = marker.getElement();
            if (!el) continue;

            const labelEl = el.querySelector<HTMLElement>('.map-marker-label');
            if (!labelEl) continue;

            const markerBox = getMarkerBoundingBox(el);
            const labelBox = getLabelBoundingBox(labelEl);

            // Check if this label would collide with any occupied box
            const hasCollision = occupiedBoxes.some(
                box => boxesOverlap(labelBox, box)
            );

            if (hasCollision && !forceShow) {
                labelEl.classList.add('map-marker-label--hidden');
                // Only add the marker box to occupied (label is hidden)
                occupiedBoxes.push(markerBox);
            } else {
                labelEl.classList.remove('map-marker-label--hidden');
                // Add both marker and label boxes to occupied
                occupiedBoxes.push(markerBox);
                occupiedBoxes.push(labelBox);
            }
        }
    }, [markerRefs, highlightedId, hoveredId, allCoordinates]);

    useEffect(() => {
        // Run on mount and when markers change
        const timeoutId = setTimeout(updateLabelVisibility, 50);

        // Listen to map events
        map.on('zoomend', updateLabelVisibility);
        map.on('moveend', updateLabelVisibility);

        return () => {
            clearTimeout(timeoutId);
            map.off('zoomend', updateLabelVisibility);
            map.off('moveend', updateLabelVisibility);
        };
    }, [map, updateLabelVisibility, markerCount]);

    return null;
};

const MapComponent: React.FC<MapComponentProps> = ({
    allCoordinates,
    focusCoordinates,
    highlightedId,
    popupInfo,
    onMarkerClick,
    onPopupClose,
    shouldResizeBounds,
    buildingSummaries,
}) => {
    const [isSatellite, setIsSatellite] = useState<boolean>(() =>
        loadFromStorage(STORAGE_KEYS.IS_SATELLITE, false)
    );
    const [showFilteredMarkers, setShowFilteredMarkers] = useState<boolean>(() =>
        loadFromStorage(STORAGE_KEYS.SHOW_FILTERED_MARKERS, false)
    );
    const [hoveredId, setHoveredId] = useState<string | undefined>(undefined);

    // Persist settings to localStorage
    useEffect(() => { saveToStorage(STORAGE_KEYS.IS_SATELLITE, isSatellite); }, [isSatellite]);
    useEffect(() => { saveToStorage(STORAGE_KEYS.SHOW_FILTERED_MARKERS, showFilteredMarkers); }, [showFilteredMarkers]);
    const markerRefs = useRef<{ [key: string]: LeafletMarker | null }>({});
    const iconCacheRef = useRef<Map<string, L.DivIcon>>(new Map());

    const focusBounds = focusCoordinates.map((loc) => loc.coords);

    useEffect(() => {
        const marker = popupInfo ? markerRefs.current[popupInfo.id] : null;
        if (!marker) return;
        const map = (marker as any)._map as L.Map | undefined;
        if (map && popupInfo!.coordinates[0] !== 0) {
            const center = map.getCenter();
            const target = L.latLng(popupInfo!.coordinates);
            const mapSize = map.getSize();
            const centerPx = map.latLngToContainerPoint(center);
            const targetPx = map.latLngToContainerPoint(target);
            const dx = Math.abs(targetPx.x - centerPx.x) / mapSize.x;
            const dy = Math.abs(targetPx.y - centerPx.y) / mapSize.y;
            if (dx > 0.2 || dy > 0.2) {
                map.setView(popupInfo!.coordinates, map.getZoom(), { animate: true, duration: 0.3 });
            }
        }
        marker.openPopup();
    }, [popupInfo]);

    useEffect(() => {
        // Pre-compute lookup map for O(1) access instead of O(n) .find() per marker
        const coordsByKey = new Map(allCoordinates.map(c => [c.building.key, c]));

        Object.entries(markerRefs.current).forEach(([id, marker]) => {
            const el = marker?.getElement();
            const inner = el?.querySelector<HTMLElement>('.map-marker-inner');
            if (inner) {
                inner.classList.toggle('map-marker--active', id === highlightedId);
            }
            // Handle filtered-out markers: show/hide based on highlighted state
            const coordItem = coordsByKey.get(id);
            const isFilteredOut = coordItem && !coordItem.isFiltered;
            if (isFilteredOut) {
                setFilteredMarkerVisibility(marker, id === highlightedId);
            }
        });
    }, [highlightedId, allCoordinates]);


    return (
        <div style={{ position: 'relative', height: '100%', width: '100%' }}>
            <MapContainer
                key={'map-container'}
                center={[59.3293, 18.0686]}
                zoom={13}
                maxZoom={18}
                style={{ height: '100%', width: '100%' }}
            >
                {!isSatellite ? (
                    <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        maxZoom={18}
                    />
                ) : (
                    <TileLayer
                        url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                        attribution='&copy; <a href="https://www.esri.com/">Esri</a> contributors'
                        maxZoom={18}
                    />
                )}

                {focusBounds.length > 0 && <FitBoundsHandler bounds={focusBounds} shouldResizeBounds={shouldResizeBounds} />}
                <LabelCollisionHandler markerRefs={markerRefs} markerCount={allCoordinates.length} highlightedId={highlightedId} hoveredId={hoveredId} allCoordinates={allCoordinates} />

                {allCoordinates
                    .filter((item) => item.isFiltered || showFilteredMarkers)
                    .map((item) => {
                        const isFilteredOut = !item.isFiltered;
                        const isHighlighted = item.building.key === highlightedId;

                        // Use grey color for filtered-out markers
                        const color = isFilteredOut ? MARKER_COLORS.greyed : MARKER_COLORS.active;
                        const symbolUrl = markerSymbolUrl(item.building);
                        const label = item.building.name;

                        // Create icon (label visibility managed dynamically via setFilteredMarkerVisibility)
                        const cacheKey = `${color}-${symbolUrl}-${label ?? ''}`;
                        let iconToUse = iconCacheRef.current.get(cacheKey);
                        if (!iconToUse) {
                            iconToUse = createSvgMarker(color, symbolUrl, label);
                            iconCacheRef.current.set(cacheKey, iconToUse);
                        }

                        // Filtered-out markers render behind active markers
                        const zIndexOffset = isFilteredOut
                            ? MARKER_Z_INDEX.filteredOut
                            : (isHighlighted ? MARKER_Z_INDEX.highlighted : MARKER_Z_INDEX.normal);

                        return (
                            <Marker
                                key={item.building.key}
                                position={item.coords}
                                icon={iconToUse}
                                opacity={isFilteredOut && !isHighlighted ? MARKER_OPACITY.filteredOut : MARKER_OPACITY.full}
                                ref={(el) => {
                                    markerRefs.current[item.building.key] = el;
                                    // Set initial label visibility for filtered-out markers
                                    if (isFilteredOut && !isHighlighted && el) {
                                        const labelEl = el.getElement()?.querySelector<HTMLElement>('.map-marker-label');
                                        if (labelEl) labelEl.style.display = 'none';
                                    }
                                }}
                                eventHandlers={{
                                    mouseover: () => {
                                        setHoveredId(item.building.key);
                                        const marker = markerRefs.current[item.building.key];
                                        const inner = marker?.getElement()?.querySelector<HTMLElement>('.map-marker-inner');
                                        inner?.classList.add('map-marker--hover');
                                        // Bring filtered-out marker to full opacity and show label
                                        if (isFilteredOut) {
                                            setFilteredMarkerVisibility(marker, true);
                                        }
                                    },
                                    mouseout: () => {
                                        setHoveredId(undefined);
                                        const marker = markerRefs.current[item.building.key];
                                        const inner = marker?.getElement()?.querySelector<HTMLElement>('.map-marker-inner');
                                        inner?.classList.remove('map-marker--hover');
                                        // Return filtered-out marker to reduced opacity and hide label (unless highlighted)
                                        if (isFilteredOut && highlightedId !== item.building.key) {
                                            setFilteredMarkerVisibility(marker, false);
                                        }
                                    },
                                    mousedown: () => {
                                        // Set active immediately on mousedown (before mouseout fires) to prevent bounce
                                        Object.entries(markerRefs.current).forEach(([id, marker]) => {
                                            const inner = marker?.getElement()?.querySelector<HTMLElement>('.map-marker-inner');
                                            if (inner) {
                                                inner.classList.toggle('map-marker--active', id === item.building.key);
                                            }
                                        });
                                    },
                                    click: () => onMarkerClick?.(item.building.key),
                                }}
                                zIndexOffset={zIndexOffset}
                            >
                                <MapPopup
                                    building={item.building}
                                    onClose={onPopupClose}
                                />
                            </Marker>
                        );
                    })}
            </MapContainer>

            <Dropdown align="end" style={{ position: 'absolute', top: 10, right: 10, zIndex: 1000 }} className="map-layers-dropdown">
                <Dropdown.Toggle variant="light" style={{ boxShadow: '0 2px 6px rgba(0,0,0,0.3)' }}>
                    <img src="/Images/layers.png" alt="Layers" style={{ width: 24, height: 24 }} />
                </Dropdown.Toggle>
                <Dropdown.Menu>
                    <Dropdown.Item onClick={() => setIsSatellite(false)} active={!isSatellite} className="map-layers-item">
                        Karta
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => setIsSatellite(true)} active={isSatellite} className="map-layers-item">
                        Satellit
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <div style={{ padding: '4px 16px' }}>
                        <Form.Check
                            type="checkbox"
                            label="Visa dolda"
                            checked={showFilteredMarkers}
                            onChange={(e) => setShowFilteredMarkers(e.target.checked)}
                        />
                    </div>
                </Dropdown.Menu>
            </Dropdown>
        </div>
    );
};

export default MapComponent;